import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  RiCursorLine,
  RiTeamLine,
  RiBellLine,
  RiArrowRightLine,
  RiNodeTree,
  RiUserLine,
  RiPlayLine,
  RiCheckLine,
} from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

function CollaborationContent() {
  return (
    <>
      <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 z-10">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <RiArrowRightLine className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>Collaboration</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="ml-auto">
          <ModeToggle />
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        <div className="container max-w-6xl py-8 space-y-8">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center space-y-4"
          >
            <Badge className="mb-2" variant="outline">
              Real-Time Collaboration
            </Badge>
            <h1 className="text-4xl font-bold tracking-tight">
              Work Together in Real-Time
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Moniva is like Figma for databases. See your teammates in action, share insights instantly,
              and collaborate seamlessly across your organization.
            </p>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid md:grid-cols-3 gap-6"
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                  <RiCursorLine className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Live Cursor Tracking</CardTitle>
                <CardDescription>
                  See your teammates' cursors moving in real-time on the schema canvas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Watch teammates navigate the schema</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>See exactly where they're looking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Smooth 60fps cursor updates</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-500/10 mb-4">
                  <RiTeamLine className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Presence Indicators</CardTitle>
                <CardDescription>
                  Know who's viewing each database at any moment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Avatar stack in query header</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Viewer count updates live</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Join/leave notifications</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/10 mb-4">
                  <RiBellLine className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle className="text-lg">Query Notifications</CardTitle>
                <CardDescription>
                  Get notified when teammates run queries
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>See query execution alerts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>View performance metrics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <RiCheckLine className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Click to see the query</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">How to Use Collaboration Features</CardTitle>
                <CardDescription>
                  Follow these steps to collaborate with your team
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1 flex items-center gap-2">
                        <RiTeamLine className="w-4 h-4" />
                        Invite Your Team
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Go to <strong>Team</strong> page and invite members to your organization.
                        They'll get access to all shared databases.
                      </p>
                      <Link to="/team">
                        <Button variant="outline" size="sm" className="mt-2 gap-2">
                          <RiTeamLine className="w-4 h-4" />
                          Go to Team Page
                          <RiArrowRightLine className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex gap-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1 flex items-center gap-2">
                        <RiNodeTree className="w-4 h-4" />
                        Open a Database Schema
                      </h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Navigate to any database and click the <strong>Schema tab</strong>.
                        This is where real-time cursor tracking happens!
                      </p>
                      <div className="p-3 rounded-lg bg-muted border">
                        <p className="text-xs font-mono text-muted-foreground">
                          💡 Tip: Ask a teammate to open the same database and watch their cursor appear on the canvas
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex gap-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1 flex items-center gap-2">
                        <RiPlayLine className="w-4 h-4" />
                        Run Queries Together
                      </h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Open the <strong>Query Editor tab</strong>. When you or a teammate runs a query,
                        everyone viewing the database gets a notification.
                      </p>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                        <RiUserLine className="w-4 h-4 text-blue-600 shrink-0" />
                        <p className="text-xs text-blue-900 dark:text-blue-100">
                          <strong>John Doe</strong> executed query • Completed in 45ms
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center space-y-4 py-8"
          >
            <h2 className="text-2xl font-bold">Ready to Collaborate?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Open any database and invite your team to experience real-time collaboration
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/databases">
                <Button size="lg" className="gap-2">
                  <RiNodeTree className="w-5 h-5" />
                  View Databases
                  <RiArrowRightLine className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/team">
                <Button size="lg" variant="outline" className="gap-2">
                  <RiTeamLine className="w-5 h-5" />
                  Invite Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    </>
  );
}

export function CollaborationPage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <CollaborationContent />
      </SidebarInset>
    </SidebarProvider>
  );
}
